function _extends() { _extends = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

function _defineProperty(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

import React, { useState, useEffect, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { FunctionalLoader, useGlobalContext, Toast } from 'seagull';
import { Page, Typography, Card, Switch, Grid, GridItem, Button, WithInfoIcon, WithStatusIcon, ConfirmationModal } from 'seagull';
import { useEventReporter } from "../../../../event-reporter";
import { useStore, useStoreActions, useStoreApi } from "../../../../store/store-context";
import { useIntegrations } from "../../../../hooks/useIntegrations";
import { openInNewTabProps, routes } from "../../../../constants";
import { RefreshQboButton } from "../RefreshQboButton/RefreshQboButton";
import { selectors } from "../../../../store/state-selectors";
import { integrationsModel } from "../../../../models/integrations";
import { Intent } from "../../../../infra/access-control";
import { MapServicesModal } from "../MapServicesModal/MapServicesModal";
import { MapContactsModal } from "../MapContactsModal/MapContactsModal";
import { MapAccountsModal } from "../MapAccountsModal/MapAccountsModal";
import styles from "./QboSetupPage.module.scss";
import { getIntercomHelpArticle } from "../../../../infra/utils";
import { timeModel } from "../../../../models";
import ContactSupportButton from "../../../ContactSupportButton";
const title = 'QuickBooks Online Setup';
const subheader = 'Set up your QuickBooks Online account connection.';
export function QboSetupPage(_ref) {
  let {
    refreshPollingInterval
  } = _ref;
  const {
    showToast,
    showModal
  } = useGlobalContext();
  const {
    getState
  } = useStoreApi();
  const isPendingQboIntegrationSetup = useStore(selectors.integration.isPendingQboIntegrationSetup);
  const vendorId = useStore(selectors.user.businessId);
  const userId = useStore(selectors.user.id);
  const {
    updateIntegrationSettings,
    disconnectIntegration
  } = useIntegrations();
  const [hasOperationInBackground, setHasOperationInBackground] = useState(false);
  const history = useHistory();
  const [qboIntegration, setQboIntegration] = useState(() => selectors.integration.qboIntegration(getState()));
  const [isLoading, setIsLoading] = useState(false);
  const {
    reportToAppcues,
    reportToGtm,
    reportToHeap,
    reportToHubspot
  } = useEventReporter();
  const {
    logBI,
    fetchIntegrations
  } = useStoreActions();

  const fetchAndSetIntegration = async () => {
    const integrations = await fetchIntegrations();
    const newQboIntegration = integrationsModel.qboIntegration(integrations);
    setQboIntegration(newQboIntegration);
    return newQboIntegration;
  };

  useEffect(() => {
    setIsLoading(true);
    fetchAndSetIntegration().then(qboInteg => {
      const hasIntegration = Boolean(qboInteg);
      const isIntegrationLinkedAndNotReady = (qboInteg === null || qboInteg === void 0 ? void 0 : qboInteg.status) === 'linked' && !(qboInteg !== null && qboInteg !== void 0 && qboInteg.isReady);

      if (!hasIntegration || isIntegrationLinkedAndNotReady) {
        history.push(routes.integrations);
      }
    }).finally(() => setIsLoading(false));
  }, []);
  const subHeader = useMemo(() => getSubHeaderByIntegration(qboIntegration), [qboIntegration]);

  const disconnect = async () => {
    const userApprovedDisconnect = await showModal({
      closeOnClickOutside: false,
      closeOnEscapeKey: false,
      content: _ref2 => {
        let {
          dismissModal
        } = _ref2;
        return /*#__PURE__*/React.createElement(ConfirmationModal, {
          "data-testid": "qbo-disconnect-modal",
          title: "Disconnect from QuickBooks Online",
          content: /*#__PURE__*/React.createElement(Typography, null, "Are you sure you want to disconnect from QuickBooks Online?"),
          onCloseButtonClick: () => dismissModal(false),
          onPrimaryButtonClick: () => dismissModal(true),
          primaryButtonLabel: "Disconnect",
          onSecondaryButtonClick: () => dismissModal(false),
          secondaryButtonLabel: "Cancel",
          skin: "danger"
        });
      }
    });

    if (userApprovedDisconnect) {
      logBI(12003, {
        vendorId,
        userId
      });
      reportToAppcues('button-click', 'qbo-vendor-disconnect-connection');
      reportToGtm('button-click', 'qbo-vendor-disconnect-connection', {
        vendorId,
        userId
      });
      reportToHubspot('vendor_disconnects_qbo_connection');
      await disconnectIntegration();
      history.push(routes.integrations);
    }
  };

  const toggleAutoSyncInvoices = async toggle => {
    if (!qboIntegration) return;
    setHasOperationInBackground(true);
    const [, error] = await updateIntegrationSettings({
      automaticInvoicesSync: toggle
    });

    if (!error) {
      const newQboIntegration = _objectSpread(_objectSpread({}, qboIntegration), {}, {
        settings: {
          automaticInvoicesSync: toggle
        }
      });

      setQboIntegration(newQboIntegration);
      setHasOperationInBackground(false);
    }
  };

  const chooseAccounts = async () => {
    logBI(12010, {
      vendorId,
      userId
    });
    reportToHeap('integrations', 'click', 'qbo-choose-accounts');
    reportToHubspot('vendor_started_contact_sync');
    await showModal({
      closeOnClickOutside: false,
      closeOnEscapeKey: false,
      content: _ref3 => {
        let {
          dismissModal
        } = _ref3;
        return /*#__PURE__*/React.createElement(MapAccountsModal, {
          dismissModal: dismissModal
        });
      }
    });
  };

  const mapContacts = async () => {
    logBI(12001, {
      vendorId,
      userId
    });
    reportToAppcues('button-click', 'qbo-vendor-started-contacts-map');
    reportToGtm('button-click', 'qbo-vendor-started-contacts-map', {
      vendorId,
      userId
    });
    reportToHeap('integrations', 'click', 'qbo-map-contacts');
    reportToHubspot('vendor_started_contact_sync');
    await showModal({
      closeOnClickOutside: false,
      closeOnEscapeKey: false,
      content: _ref4 => {
        let {
          dismissModal
        } = _ref4;
        return /*#__PURE__*/React.createElement(MapContactsModal, {
          dismissModal: dismissModal
        });
      }
    });
  };

  const mapServices = async () => {
    logBI(12002, {
      vendorId,
      userId
    });
    reportToAppcues('button-click', 'qbo-vendor-started-services-map');
    reportToGtm('button-click', 'qbo-vendor-started-services-map', {
      vendorId,
      userId
    });
    reportToHeap('integrations', 'click', 'qbo-map-services');
    reportToHubspot('vendor_started_services_sync');
    await showModal({
      closeOnClickOutside: false,
      closeOnEscapeKey: false,
      content: _ref5 => {
        let {
          dismissModal
        } = _ref5;
        return /*#__PURE__*/React.createElement(MapServicesModal, {
          dismissModal: dismissModal
        });
      }
    });
  };

  return /*#__PURE__*/React.createElement(Page, {
    "data-testid": "qbo-setup-page",
    header: /*#__PURE__*/React.createElement("div", {
      className: styles.pageHeader
    }, title),
    subheader: /*#__PURE__*/React.createElement("div", {
      className: styles.pageSubHeader
    }, subheader, /*#__PURE__*/React.createElement(Typography, _extends({
      as: "a",
      href: getIntercomHelpArticle('6745076-setting-up-quickbooks-online')
    }, openInNewTabProps), "Learn more")),
    loading: isLoading,
    onClickBack: () => {
      history.push(routes.integrations);
    }
  }, /*#__PURE__*/React.createElement(FunctionalLoader, {
    show: hasOperationInBackground,
    name: "integrationOperation"
  }), /*#__PURE__*/React.createElement("div", {
    className: styles.pageContainer
  }, /*#__PURE__*/React.createElement(Section, {
    testid: "connection-settings",
    header: "Connection settings",
    subHeader: subHeader,
    isError: !qboIntegration || (qboIntegration === null || qboIntegration === void 0 ? void 0 : qboIntegration.status) === 'error'
  }, /*#__PURE__*/React.createElement(Grid, {
    rowGap: "small"
  }, /*#__PURE__*/React.createElement(GridItem, {
    span: 10
  }, /*#__PURE__*/React.createElement("div", {
    className: styles.accountRow
  }, isPendingQboIntegrationSetup ? /*#__PURE__*/React.createElement(WithStatusIcon, {
    "data-testid": "missing-account-mapping-error",
    info: "Selecting accounts is mandatory for this integration to work"
  }, /*#__PURE__*/React.createElement(Typography, null, "Select which QuickBooks accounts will be used")) : /*#__PURE__*/React.createElement(Typography, null, "Select which QuickBooks accounts will be used"))), /*#__PURE__*/React.createElement(GridItem, {
    span: 2
  }, /*#__PURE__*/React.createElement(Button, {
    className: styles.button,
    "data-testid": "choose-accounts",
    intent: Intent.QboMapAccounts,
    onClick: chooseAccounts,
    disabled: (qboIntegration === null || qboIntegration === void 0 ? void 0 : qboIntegration.status) === 'error'
  }, "Choose accounts")), /*#__PURE__*/React.createElement(GridItem, {
    span: 10
  }, /*#__PURE__*/React.createElement(WithInfoIcon, {
    info: "New invoices will be synced automatically. If turned off, a manual sync option will be available on each invoice."
  }, /*#__PURE__*/React.createElement(Typography, null, "Sync invoices automatically"))), /*#__PURE__*/React.createElement(GridItem, {
    span: 2
  }, /*#__PURE__*/React.createElement(Switch, {
    checked: qboIntegration === null || qboIntegration === void 0 ? void 0 : qboIntegration.settings.automaticInvoicesSync,
    disabled: (qboIntegration === null || qboIntegration === void 0 ? void 0 : qboIntegration.status) !== 'linked' || isPendingQboIntegrationSetup,
    "data-testid": "sync-invoices-automatically-switch",
    onChange: (_, checked) => toggleAutoSyncInvoices(checked)
  })), integrationsModel.isConnectedViaCodat(qboIntegration) && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(GridItem, {
    span: 10
  }, /*#__PURE__*/React.createElement(Typography, null, "Refresh QuickBooks Online data")), /*#__PURE__*/React.createElement(GridItem, {
    span: 2
  }, /*#__PURE__*/React.createElement(RefreshQboButton, {
    className: styles.button,
    pollingInterval: refreshPollingInterval,
    onError: errorMessage => {
      showToast( /*#__PURE__*/React.createElement(Toast, {
        type: "error",
        testid: "refresh-quickbooks-error-toast",
        text: errorMessage || ''
      }));
    },
    onSuccess: integration => {
      setQboIntegration(integration);
    }
  }))))), (qboIntegration === null || qboIntegration === void 0 ? void 0 : qboIntegration.status) === 'linked' && /*#__PURE__*/React.createElement(Section, {
    testid: "contacts-and-services",
    header: "Contacts and services",
    subHeader: "For invoices sync to work, relevant contacts and services need to be mapped to their QuickBooks Online counterparts."
  }, /*#__PURE__*/React.createElement(Grid, {
    rowGap: "small"
  }, /*#__PURE__*/React.createElement(GridItem, {
    span: 10
  }, /*#__PURE__*/React.createElement(Typography, null, "Map Anchor contacts to QuickBooks customers")), /*#__PURE__*/React.createElement(GridItem, {
    span: 2
  }, /*#__PURE__*/React.createElement(Button, {
    className: styles.button,
    disabled: isPendingQboIntegrationSetup,
    "data-testid": "map-contacts",
    intent: Intent.QboSetupMapContacts,
    onClick: mapContacts
  }, "Map contacts")), /*#__PURE__*/React.createElement(GridItem, {
    span: 10
  }, /*#__PURE__*/React.createElement(Typography, null, "Map Anchor services to QuickBooks services")), /*#__PURE__*/React.createElement(GridItem, {
    span: 2
  }, /*#__PURE__*/React.createElement(Button, {
    className: styles.button,
    disabled: isPendingQboIntegrationSetup,
    "data-testid": "map-services",
    intent: Intent.QboMapServices,
    onClick: mapServices
  }, "Map services")))), /*#__PURE__*/React.createElement(Section, {
    testid: "disconnect-qbo"
  }, /*#__PURE__*/React.createElement(Grid, {
    align: "center"
  }, /*#__PURE__*/React.createElement(GridItem, {
    span: 10
  }, /*#__PURE__*/React.createElement(Typography, {
    skin: "error"
  }, "Disconnect from QuickBooks Online")), /*#__PURE__*/React.createElement(GridItem, {
    span: 2
  }, /*#__PURE__*/React.createElement(Button, {
    className: styles.button,
    skin: "danger",
    "data-testid": "disconnect",
    intent: Intent.QboDisconnect,
    onClick: disconnect,
    disabled: (qboIntegration === null || qboIntegration === void 0 ? void 0 : qboIntegration.status) === 'error'
  }, "Disconnect"))))));
}

const getSubHeaderByIntegration = integration => {
  switch (integration === null || integration === void 0 ? void 0 : integration.status) {
    case 'paused':
      return 'Connection is paused.';

    case 'linked':
      {
        return integrationsModel.isConnectedViaCodat(integration) ? "Last sync ".concat(timeModel.formatDateWithTime(integration.lastSync), ".") : '';
      }

    case 'error':
      return /*#__PURE__*/React.createElement(React.Fragment, null, "Failed to connect. Please ", /*#__PURE__*/React.createElement(ContactSupportButton, {
        skin: "error",
        size: "small"
      }), ".");

    default:
      return '';
  }
};

const Section = props => {
  const {
    testid,
    header,
    subHeader,
    isError,
    children
  } = props;
  return /*#__PURE__*/React.createElement(Card, {
    "data-testid": "".concat(testid, "-section"),
    className: styles.section
  }, header && /*#__PURE__*/React.createElement("div", {
    className: styles.header
  }, /*#__PURE__*/React.createElement(Typography, {
    "data-testid": "".concat(testid, "-header"),
    weight: "normal",
    variant: "h5"
  }, header), subheader && /*#__PURE__*/React.createElement(Typography, {
    skin: isError ? 'error' : 'secondary',
    variant: "paragraph2",
    "data-testid": "".concat(testid, "-sub-header")
  }, subHeader)), /*#__PURE__*/React.createElement("div", null, children));
};